import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import { getRelativeDateLocalization } from '../../../helpers/localization/localizationUtils';
import { modifiers, isPastDate } from '../../../helpers/dayCard/dayCard';

import Box from '../../elements/Box';
import Text from '../../elements/Text';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import FareItemMiles from '../_commons/atoms/FareItemMiles';
import FareItemNoPriceAvailable from '../_commons/atoms/FareItemNoPriceAvailable';
import { handleFareItemInteraction } from '../_commons/commonFns';
import ErrorBoundary from '../../utils/ErrorBoundary';

const DayCardMobile = (props) => {
  const {
    day,
    data,
    showPrice,
    showMiles,
    loading,
    isLowest,
    usedErrorBoundary,
    translate,
    disregardedBehavior,
    isDisregarded,
    isOutside,
    enabledCallToAction = true,
    handlerClick
  } = props;

  const dayCardMobile = modifiers(props);
  const { localizationData } = useContext(Context);

  let dataTestId = 'dayCard';
  if (isOutside) {
    dataTestId = 'dayCard--outside';
  }
  if (isDisregarded) {
    dataTestId = 'dayCard--disregarded';
  }
  if (isPastDate(day)) {
    dataTestId = 'dayCard--pastdate';
  }
  const isInteractive = !(isPastDate(day) || isDisregarded || isOutside);

  const {
    handleCallToAction,
    handleClick,
    handleItemKeyDown,
    handleCallToActionKeyDown
  } = handleFareItemInteraction(props);

  const cardRender = (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="between"
      paddingHeight="2"
      alignItems="center"
      tabIndex={isInteractive ? 0 : null}
      {...dayCardMobile}
      role={isInteractive ? 'button' : undefined}
      onClick={() => {
        return enabledCallToAction ? handleClick() : handlerClick(day);
      }}
      onKeyDown={handleItemKeyDown}
      dataTestId={dataTestId}
    >
      <Text
        className="fr-sr-only"
        ariaLabel={
          isLowest ? translate('cmp-day-card-lowest-price', 'Lowest price of the month') : null
        }
      />
      {!loading && dayCardMobile.showDay && (
        <Text
          {...dayCardMobile.dayCardMobileDay}
          role="heading"
          ariaLevel="3"
          ariaLabel={dayjs(day)
            .locale(getRelativeDateLocalization(translate, localizationData), null, true)
            .format('MMMM D')}
        >
          {dayjs(day).date()}
        </Text>
      )}
      {!isPastDate(day) && (
        <>
          {!loading && data && data.dataType === 'DATA_API' && (
            <>
              {showPrice && <FareItemPrice {...data} {...dayCardMobile.dayCardMobilePrice} />}
              {showMiles && <FareItemMiles {...data} {...dayCardMobile.dayCardMobileMiles} />}
            </>
          )}
          {!loading &&
            ((data && data.dataType && data.dataType !== 'DATA_API') ||
              (isDisregarded && disregardedBehavior === 'glass-date')) && (
              <FareItemNoPriceAvailable
                showMessage={false}
                {...dayCardMobile.dayCardMobileNoPriceAvailable}
                handleClick={handleCallToAction}
                onKeyDown={handleCallToActionKeyDown}
              />
            )}
        </>
      )}
    </Box>
  );

  return usedErrorBoundary ? <ErrorBoundary>{cardRender}</ErrorBoundary> : { cardRender };
};

DayCardMobile.propTypes = {
  /**
   * Cursor type
   */
  cursorType: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * The day to render
   */
  day: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the day has the lowest price
   */
  isLowest: PropTypes.bool,
  /**
   * Determine if the day is outside.
   */
  isOutside: PropTypes.bool,
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMobileDay
   */
  dayCardMobileDay: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMobilePrice
   */
  dayCardMobilePrice: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMobileNoPriceAvailable
   */
  dayCardMobileNoPriceAvailable: PropTypes.objectOf(PropTypes.any),
  /**
   * Turn off or on the showPrice section
   */
  showPrice: PropTypes.bool,
  /**
   * Turn off or on the showDay section
   */
  showDay: PropTypes.bool,
  /**
   * Event fired when key down element
   */
  handleDayKeyDown: PropTypes.func,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * Props of currentDayStyles
   */
  currentDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of lowestPriceStyles
   */
  lowestPriceStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of pastDayStyles
   */
  pastDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of outsideDayStyles
   */
  outsideDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interactionHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Props of noPriceAvailableStyles
   */
  noPriceAvailableStyles: PropTypes.objectOf(PropTypes.any),
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date']),
  isDisregarded: PropTypes.bool
};

DayCardMobile.defaultProps = {
  onClick: () => {},
  cursorType: 'pointer',
  translate: () => {},
  day: null,
  isLowest: false,
  isOutside: false,
  data: null,
  dayCardMobileDay: null,
  dayCardMobilePrice: null,
  dayCardMobileNoPriceAvailable: null,
  showPrice: true,
  showDay: true,
  handleDayKeyDown: () => {},
  loading: false,
  usedErrorBoundary: true,
  interactionHotZone: 'smart',
  currentDayStyles: {
    borderColor: 'primary',
    border: true
  },
  pastDayStyles: {
    disabled: true,
    bgColor: 'light',
    textColor: 'dark-6'
  },
  outsideDayStyles: {
    showDay: false,
    bgColor: 'light',
    cursor: 'default'
  },
  lowestPriceStyles: {
    bgColor: 'lowestFareBg',
    textColor: 'lowestFareText',
    fontWeight: 'bold'
  },
  noPriceAvailableStyles: {},
  disregardedBehavior: 'past-date',
  isDisregarded: false
};

export default DayCardMobile;
